<template>
    <div class="motoristas-gantt">
        <div class="d-flex align-center flex-wrap mb-2">
            <span v-for="item in legend" :key="item.tipo" class="motoristas-gantt__legend-item text-caption">
                <span
                    class="motoristas-gantt__swatch"
                    :class="{ 'motoristas-gantt__swatch--idle': item.tipo === 'espera' }"
                    :style="{ background: item.color }"
                />
                {{ item.label }}
            </span>
            <v-spacer />
            <v-switch
                v-model="sortByCost"
                dense
                hide-details
                class="mt-0 pt-0"
                :label="$t('routePlanning.drivers.jornada.sortByCost')"
            />
        </div>

        <p v-if="!rows.length" class="text-caption mb-0">{{ $t('routePlanning.drivers.jornada.empty') }}</p>
        <div v-else class="motoristas-gantt__scroll">
            <div class="motoristas-gantt__grid" :style="{ minWidth: `${LABEL_W + hours.length * HOUR_PX}px` }">
                <div class="motoristas-gantt__row motoristas-gantt__row--axis">
                    <div class="motoristas-gantt__label motoristas-gantt__label--head text-caption">
                        {{ $t('routePlanning.drivers.jornada.driverAxis') }}
                    </div>
                    <div class="motoristas-gantt__track">
                        <span
                            v-for="h in hours"
                            :key="h.at"
                            class="motoristas-gantt__tick"
                            :style="{ left: pos(h.at), width: pct(60) }"
                        >{{ h.label }}</span>
                    </div>
                </div>
                <div class="motoristas-gantt__body">
                    <div v-for="row in rows" :key="row.id" class="motoristas-gantt__row">
                        <div class="motoristas-gantt__label" :title="row.title">
                            <div class="motoristas-gantt__label-main">
                                #{{ row.id }} · {{ row.typeLabel }}
                                <v-icon v-if="row.warning" x-small color="warning" class="ml-1">mdi-alert</v-icon>
                            </div>
                            <div class="motoristas-gantt__label-sub">{{ row.sub }}</div>
                        </div>
                        <div class="motoristas-gantt__track">
                            <div
                                v-for="(piece, p) in row.envelope"
                                :key="`e${p}`"
                                class="motoristas-gantt__envelope"
                                :style="{ left: pos(piece.ini), width: pct(piece.fim - piece.ini), background: colors.espera }"
                                :title="row.title"
                            />
                            <div
                                v-for="piece in row.pieces"
                                :key="piece.key"
                                class="motoristas-gantt__segment"
                                :style="{ left: pos(piece.ini), width: pct(piece.fim - piece.ini), background: colors[piece.seg.tipo] }"
                                :title="segmentTitle(row, piece.seg)"
                            />
                        </div>
                    </div>
                    <div class="motoristas-gantt__gridlines" :style="{ left: `${LABEL_W}px` }">
                        <div
                            v-for="h in hours"
                            :key="`g${h.at}`"
                            class="motoristas-gantt__gridline"
                            :class="{ 'motoristas-gantt__gridline--noon': h.noon }"
                            :style="{ left: pos(h.at) }"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    DRIVER_SEGMENT_COLORS,
    DRIVER_SEGMENT_TYPES,
    driverTimeline,
    minToClock,
    minToDuration,
    typeKeyLabel,
    wrapIntoDay,
} from '@/utils/roteirizacaoMotoristas';

const LABEL_W = 156;
// Largura mínima por hora: no card lateral (~560px) cabe boa parte do dia; mais largo, estica.
const HOUR_PX = 26;

/**
 * Gantt dos motoristas: uma linha por motorista, eixo x com 24 colunas (00h → 23h). O que passa
 * de 24:00 (virada de dia) volta ao início do gráfico. Cores por tipo de segmento; o fundo entre
 * o início e o fim da jornada é espera.
 */
export default {
    name: 'RoteirizacaoMotoristasjornada',
    props: {
        /** `schedule.motoristas` do worker ROTEIRIZACAO_MOTORISTAS. */
        drivers: { type: Array, default: () => [] },
    },
    data() {
        return {
            LABEL_W,
            HOUR_PX,
            colors: DRIVER_SEGMENT_COLORS,
            // Ligado: maior custo primeiro; desligado: ordem de início da jornada.
            sortByCost: false,
        };
    },
    computed: {
        lines() {
            return this.drivers
                .map((m) => ({ m, line: driverTimeline(m) }))
                .filter((x) => x.line);
        },
        range() {
            return { start: 0, end: 1440 };
        },
        hours() {
            const out = [];
            for (let at = this.range.start; at < this.range.end; at += 60) {
                out.push({ at, label: minToClock(at).slice(0, 2), noon: ((at % 1440) + 1440) % 1440 === 720 });
            }
            return out;
        },
        rows() {
            const rows = this.lines.map(({ m, line }) => {
                const j = m.jornada || {};
                const total = j.totalMin != null ? j.totalMin : null;
                const lunchMissing = j.almocoExigido && !j.almoco;
                const sub = total != null
                    ? `${minToDuration(total)} · ${this.$t('routePlanning.drivers.jornada.netShort')} ${minToDuration(j.liquidaMin)}`
                    : `${this.$t('routePlanning.drivers.jornada.netShort')} ${minToDuration(j.liquidaMin)}`;
                return {
                    id: m.id,
                    cost: Number(m.custo) || 0,
                    typeLabel: typeKeyLabel(m.modalPrincipal),
                    start: line.start,
                    end: line.end,
                    envelope: wrapIntoDay(line.start, line.end),
                    pieces: line.segments.flatMap((seg, i) => wrapIntoDay(seg.ini, seg.fim)
                        .map((piece, k) => ({ key: `${i}-${k}`, seg, ...piece }))),
                    sub,
                    warning: lunchMissing || j.ok === false,
                    title: this.rowTitle(m, line),
                };
            });
            return rows.sort(this.sortByCost
                ? (a, b) => b.cost - a.cost || a.start - b.start || a.id - b.id
                : (a, b) => a.start - b.start || a.id - b.id);
        },
        legend() {
            return [...DRIVER_SEGMENT_TYPES, 'espera'].map((tipo) => ({
                tipo,
                color: DRIVER_SEGMENT_COLORS[tipo],
                label: this.$t(`routePlanning.drivers.jornada.types.${tipo}`),
            }));
        },
    },
    methods: {
        pos(min) {
            return this.pct(min - this.range.start);
        },
        pct(min) {
            return `${(Math.max(0, min) / (this.range.end - this.range.start)) * 100}%`;
        },
        rowTitle(m, line) {
            const j = m.jornada || {};
            const parts = [
                `#${m.id} · ${typeKeyLabel(m.modalPrincipal)}`,
                `${minToClock(line.start)}–${minToClock(line.end)}`,
            ];
            if (j.totalMin != null) {
                parts.push(`${this.$t('routePlanning.drivers.totalShift')}: ${minToDuration(j.totalMin)}`
                    + (j.limiteTotalMin ? ` / ${minToDuration(j.limiteTotalMin)}` : ''));
            }
            parts.push(`${this.$t('routePlanning.drivers.netShift')}: ${minToDuration(j.liquidaMin)}`
                + (j.limiteMin ? ` / ${minToDuration(j.limiteMin)}` : ''));
            if (j.esperaMin != null) {
                parts.push(`${this.$t('routePlanning.drivers.jornada.types.espera')}: ${minToDuration(j.esperaMin)}`);
            }
            if (j.almocoExigido && !j.almoco) parts.push(this.$t('routePlanning.drivers.lunchMissing'));
            return parts.join('\n');
        },
        segmentTitle(row, seg) {
            const parts = [
                `#${row.id} · ${this.$t(`routePlanning.drivers.jornada.types.${seg.tipo}`)}`,
                `${minToClock(seg.ini)}–${minToClock(seg.fim)} (${minToDuration(seg.fim - seg.ini)})`,
            ];
            if (seg.rotas && seg.rotas.length) {
                const dir = seg.sentido ? ` · ${this.$t(`routePlanning.direction.${seg.sentido}`)}` : '';
                parts.push(`${this.$t('routePlanning.drivers.xlsx.route')}: ${seg.rotas.join(', ')}${dir}`);
            }
            if (seg.origem) {
                const key = `routePlanning.drivers.transfer.${seg.origem}`;
                parts.push(this.$te(key) ? this.$t(key) : seg.origem);
            }
            return parts.join('\n');
        },
    },
};
</script>

<style scoped>
.motoristas-gantt__legend-item {
    display: inline-flex;
    align-items: center;
    margin-right: 12px;
    margin-bottom: 4px;
}
.motoristas-gantt__swatch {
    display: inline-block;
    width: 12px;
    height: 10px;
    margin-right: 4px;
    border-radius: 2px;
}
.motoristas-gantt__swatch--idle {
    border: 1px solid rgba(0, 0, 0, 0.2);
}
.motoristas-gantt__scroll {
    overflow-x: auto;
    max-height: 420px;
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}
.motoristas-gantt__grid {
    position: relative;
}
.motoristas-gantt__body {
    position: relative;
}
.motoristas-gantt__row {
    display: flex;
    align-items: stretch;
    height: 30px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.motoristas-gantt__row--axis {
    position: sticky;
    top: 0;
    z-index: 4;
    height: 22px;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.motoristas-gantt__label {
    position: sticky;
    left: 0;
    z-index: 3;
    flex: 0 0 156px;
    width: 156px;
    padding: 2px 8px;
    background: #fff;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.motoristas-gantt__label--head {
    display: flex;
    align-items: center;
    font-weight: 600;
}
.motoristas-gantt__label-main {
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
}
.motoristas-gantt__label-sub {
    font-size: 10px;
    line-height: 12px;
    color: rgba(0, 0, 0, 0.6);
}
.motoristas-gantt__track {
    position: relative;
    flex: 1 1 auto;
}
.motoristas-gantt__tick {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding-left: 3px;
    font-size: 10px;
    color: rgba(0, 0, 0, 0.6);
}
.motoristas-gantt__envelope {
    position: absolute;
    top: 7px;
    bottom: 7px;
    border-radius: 3px;
}
.motoristas-gantt__segment {
    position: absolute;
    top: 7px;
    bottom: 7px;
    min-width: 2px;
    border-radius: 2px;
    z-index: 1;
}
.motoristas-gantt__gridlines {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 2;
}
.motoristas-gantt__gridline {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(0, 0, 0, 0.07);
}
.motoristas-gantt__gridline--noon {
    background: rgba(229, 57, 53, 0.55);
}
</style>
